import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { PACE_TAGS, insertTagAtCursor } from '../../lib/paceTags';
import { calculatePaces, applyAltitudeAdjustment, pickPaceBenchmark } from '../../lib/vdot';
import { parseRunningMiles, parseTimedIntervalMiles, parseCrossTrainingMiles, parseCrossTrainingMilesRange, paceStringToSeconds, formatVolume, formatVolumeRange } from '../../lib/volumeParser';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  const day = new Date(year, month, 1).getDay(); // 0=Sun
  return day === 0 ? 6 : day - 1; // convert to Mon=0 ... Sun=6
}

function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isToday(year, month, day) {
  const today = new Date();
  return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
}

function formatModalDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export function PlanBuilder() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const now = new Date();

  const [athletes, setAthletes] = useState([]);
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const saved = sessionStorage.getItem('apex_planbuilder_month');
    return saved !== null ? parseInt(saved, 10) : now.getMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const saved = sessionStorage.getItem('apex_planbuilder_year');
    return saved !== null ? parseInt(saved, 10) : now.getFullYear();
  });

  const [plan, setPlan] = useState(null); // current plan row
  const [workouts, setWorkouts] = useState({}); // { 'YYYY-MM-DD': text }
  const [coachNotes, setCoachNotes] = useState({}); // { 'YYYY-MM-DD': text }
  const [races, setRaces] = useState({}); // { 'YYYY-MM-DD': race }
  const [easyPaceSec, setEasyPaceSec] = useState(null); // seconds per mile for selected athlete
  const [athletePaces, setAthletePaces] = useState(null); // full paces object for selected athlete
  const [boundaryWorkouts, setBoundaryWorkouts] = useState({}); // workouts from adjacent months for boundary weeks

  const [athleteHealthFlags, setAthleteHealthFlags] = useState([]); // active flags for selected athlete
  const [athleteVacations, setAthleteVacations] = useState([]); // vacation blocks overlapping selected month

  const [expandedDay, setExpandedDay] = useState(null); // 'YYYY-MM-DD'
  const [showModal, setShowModal] = useState(false);
  const [editText, setEditText] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [planError, setPlanError] = useState('');
  const [planActionSaving, setPlanActionSaving] = useState(false);

  const textareaRef = useRef(null);

  // Fetch coach's athletes on mount
  useEffect(() => {
    if (profile) fetchAthletes();
  }, [profile]);

  async function fetchAthletes() {
    const { data: aps } = await supabase
      .from('athlete_profiles')
      .select('id')
      .eq('coach_id', profile.id)
      .eq('paused', false)
      .eq('archived', false);

    if (!aps || aps.length === 0) { setAthletes([]); return; }

    const ids = aps.map(a => a.id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', ids)
      .order('full_name');

    setAthletes(profiles ?? []);
    const params = new URLSearchParams(location.search);
    const preselect = params.get('athlete');
    const saved = sessionStorage.getItem('apex_planbuilder_athlete');
    if (preselect && profiles?.find(p => p.id === preselect)) {
      setSelectedAthleteId(preselect);
    } else if (saved && profiles?.find(p => p.id === saved)) {
      setSelectedAthleteId(saved);
    } else if (profiles?.length > 0) {
      setSelectedAthleteId(profiles[0].id);
    }
  }

  // Persist selections to sessionStorage
  useEffect(() => {
    if (selectedAthleteId) sessionStorage.setItem('apex_planbuilder_athlete', selectedAthleteId);
  }, [selectedAthleteId]);
  useEffect(() => {
    sessionStorage.setItem('apex_planbuilder_month', selectedMonth);
    sessionStorage.setItem('apex_planbuilder_year', selectedYear);
  }, [selectedMonth, selectedYear]);

  // Load plan + races when athlete/month/year changes
  useEffect(() => {
    if (selectedAthleteId) {
      loadPlan();
      loadRaces();
    }
  }, [selectedAthleteId, selectedMonth, selectedYear]);

  // Load athlete's easy pace when athlete changes
  useEffect(() => {
    if (!selectedAthleteId) return;
    (async () => {
      const [{ data }, { data: apData }] = await Promise.all([
        supabase.from('benchmarks').select('id, event, time_seconds')
          .eq('athlete_id', selectedAthleteId).order('race_date', { ascending: false }).limit(20),
        supabase.from('athlete_profiles').select('training_altitude_ft, pinned_benchmark_id').eq('id', selectedAthleteId).single(),
      ]);
      const altFt = apData?.training_altitude_ft ?? null;
      const bench = pickPaceBenchmark(data, apData?.pinned_benchmark_id);
      if (bench) {
        const result = calculatePaces(bench.event, bench.time_seconds);
        let paces = result?.paces ?? null;
        if (paces) paces = applyAltitudeAdjustment(paces, altFt);
        const pace = paces?.easy?.perMile;
        setEasyPaceSec(pace ? paceStringToSeconds(pace) : null);
        setAthletePaces(paces);
      } else {
        setEasyPaceSec(null);
        setAthletePaces(null);
      }
    })();
  }, [selectedAthleteId]);

  // Load active health flags for selected athlete
  useEffect(() => {
    if (!selectedAthleteId) { setAthleteHealthFlags([]); return; }
    (async () => {
      const { data } = await supabase
        .from('health_flags')
        .select('id, type, body_part, illness_type, pain_scale, flag_date, notes')
        .eq('athlete_id', selectedAthleteId)
        .eq('resolved', false)
        .order('flag_date', { ascending: false });
      setAthleteHealthFlags(data ?? []);
    })();
  }, [selectedAthleteId]);

  // Load vacation blocks for selected athlete that overlap the selected month
  useEffect(() => {
    if (!selectedAthleteId) { setAthleteVacations([]); return; }
    (async () => {
      const monthStr = String(selectedMonth + 1).padStart(2, '0');
      const monthStart = `${selectedYear}-${monthStr}-01`;
      const monthEnd = `${selectedYear}-${monthStr}-${String(getDaysInMonth(selectedYear, selectedMonth)).padStart(2, '0')}`;
      const { data } = await supabase
        .from('athlete_vacations')
        .select('id, title, start_date, end_date, available_equipment, blackout_dates, notes')
        .eq('athlete_id', selectedAthleteId)
        .lte('start_date', monthEnd)
        .gte('end_date', monthStart)
        .order('start_date', { ascending: true });
      setAthleteVacations(data ?? []);
    })();
  }, [selectedAthleteId, selectedMonth, selectedYear]);

  // Load workouts from adjacent months for boundary weeks (Mon–Sun spanning two months)
  useEffect(() => {
    if (!selectedAthleteId || !plan) { setBoundaryWorkouts({}); return; }
    (async () => {
      // Compute boundary dates: outside cells of the current grid
      const _daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
      const _firstDay = getFirstDayOfWeek(selectedYear, selectedMonth);
      const _totalCells = Math.ceil((_firstDay + _daysInMonth) / 7) * 7;
      const outsideDates = [];
      for (let i = 0; i < _totalCells; i++) {
        const dayNum = i - _firstDay + 1;
        if (dayNum < 1 || dayNum > _daysInMonth) {
          const d = new Date(selectedYear, selectedMonth, dayNum);
          outsideDates.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
        }
      }
      if (outsideDates.length === 0) { setBoundaryWorkouts({}); return; }

      // Find adjacent month plan IDs for this athlete
      const { data: assignments } = await supabase
        .from('athlete_plan_assignments')
        .select('plan_id, plans(id, month, year)')
        .eq('athlete_id', selectedAthleteId);

      const prevM = selectedMonth === 0 ? 12 : selectedMonth;
      const prevY = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
      const nextM = selectedMonth === 11 ? 1 : selectedMonth + 2;
      const nextY = selectedMonth === 11 ? selectedYear + 1 : selectedYear;

      const adjPlanIds = (assignments ?? [])
        .filter(a => (a.plans?.month === prevM && a.plans?.year === prevY) ||
                     (a.plans?.month === nextM && a.plans?.year === nextY))
        .map(a => a.plans.id);

      if (adjPlanIds.length === 0) { setBoundaryWorkouts({}); return; }

      const { data } = await supabase
        .from('plan_workouts')
        .select('workout_date, custom_description')
        .in('plan_id', adjPlanIds)
        .in('workout_date', outsideDates);

      const map = {};
      (data ?? []).forEach(w => { map[w.workout_date] = w.custom_description ?? ''; });
      setBoundaryWorkouts(map);
    })();
  }, [selectedAthleteId, plan, selectedMonth, selectedYear]);

  async function loadRaces() {
    const monthStr = String(selectedMonth + 1).padStart(2, '0');
    const start = `${selectedYear}-${monthStr}-01`;
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const end = `${selectedYear}-${monthStr}-${String(daysInMonth).padStart(2, '0')}`;

    const { data } = await supabase
      .from('races')
      .select('id, name, race_date, priority, event')
      .eq('athlete_id', selectedAthleteId)
      .gte('race_date', start)
      .lte('race_date', end);

    const map = {};
    (data ?? []).forEach(r => { map[r.race_date] = r; });
    setRaces(map);
  }

  async function loadPlan() {
    setLoadingPlan(true);
    setPlan(null);
    setWorkouts({});
    setCoachNotes({});
    setExpandedDay(null);

    // Find plan for this athlete + month + year (any coach — plans are athlete-scoped)
    const { data: assignments } = await supabase
      .from('athlete_plan_assignments')
      .select('plan_id, plans(id, month, year, coach_id, status)')
      .eq('athlete_id', selectedAthleteId);

    // If multiple plans exist for same month (shouldn't happen after fix, but handle gracefully),
    // pick the one with the highest ID (most recently created)
    const monthMatches = (assignments ?? []).filter(a =>
      a.plans?.month === selectedMonth + 1 &&
      a.plans?.year === selectedYear
    );
    const match = monthMatches.reduce((best, a) =>
      !best || a.plans.id > best.plans.id ? a : best
    , null);

    if (match) {
      setPlan({ ...match.plans, status: match.plans.status ?? 'in_progress' });
      await loadWorkouts(match.plans.id);
    }

    setLoadingPlan(false);
  }

  async function loadWorkouts(planId) {
    const { data } = await supabase
      .from('plan_workouts')
      .select('workout_date, custom_description, coach_notes')
      .eq('plan_id', planId);

    const workoutMap = {};
    const notesMap = {};
    (data ?? []).forEach(w => {
      workoutMap[w.workout_date] = w.custom_description ?? '';
      if (w.coach_notes) notesMap[w.workout_date] = w.coach_notes;
    });
    setWorkouts(workoutMap);
    setCoachNotes(notesMap);
  }

  async function createPlan() {
    setSaving(true);
    setPlanError('');
    try {
      // Guard: check if a plan already exists for this athlete+month (any coach)
      const { data: existing } = await supabase
        .from('athlete_plan_assignments')
        .select('plan_id, plans(id, month, year, coach_id, status)')
        .eq('athlete_id', selectedAthleteId);

      const monthMatches = (existing ?? []).filter(a =>
        a.plans?.month === selectedMonth + 1 && a.plans?.year === selectedYear
      );
      if (monthMatches.length > 0) {
        // Plan already exists — just load it instead of creating a duplicate
        const best = monthMatches.reduce((b, a) => !b || a.plans.id > b.plans.id ? a : b, null);
        setPlan({ ...best.plans, status: best.plans.status ?? 'in_progress' });
        await loadWorkouts(best.plans.id);
        setSaving(false);
        return;
      }

      const { data: newPlan, error } = await supabase
        .from('plans')
        .insert({
          coach_id: profile.id,
          name: `${MONTHS[selectedMonth]} ${selectedYear} — ${athletes.find(a => a.id === selectedAthleteId)?.full_name}`,
          month: selectedMonth + 1,
          year: selectedYear,
        })
        .select()
        .single();

      if (error) {
        setPlanError(error.message);
        setSaving(false);
        return;
      }

      const { error: assignError } = await supabase.from('athlete_plan_assignments').insert({
        plan_id: newPlan.id,
        athlete_id: selectedAthleteId,
      });

      if (assignError) {
        setPlanError(assignError.message);
        setSaving(false);
        return;
      }

      // Creating a plan for an athlete opens their bill for that
      // month. Inherit tier + price from their most recent billing
      // row — no tier prompt, no publish-gate. Failures here are
      // logged but don't block plan creation (coach can still retry
      // from the billing flow).
      await ensureBillingForPlan(selectedAthleteId, selectedMonth + 1, selectedYear);

      setPlan(newPlan);
    } catch (err) {
      console.error('[createPlan] unexpected error:', err);
      setPlanError(err.message ?? 'Unexpected error');
    }
    setSaving(false);
  }

  /**
   * Idempotent "open the bill for this month" when a coach creates a
   * plan for an athlete. Copies tier + amount from the athlete's most
   * recent billing row so ongoing athletes never see a gap; skips
   * when the athlete has no billing history yet (the first row is a
   * one-time coach setup in the admin UI).
   */
  async function ensureBillingForPlan(athleteId, month, year) {
    const { data: existing } = await supabase
      .from('billing')
      .select('id')
      .eq('athlete_id', athleteId)
      .eq('month', month)
      .eq('year', year)
      .maybeSingle();
    if (existing) return;

    const [recentRes, apRes] = await Promise.all([
      supabase
        .from('billing')
        .select('tier, amount_cents')
        .eq('athlete_id', athleteId)
        .order('year', { ascending: false })
        .order('month', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('athlete_profiles')
        .select('paused, custom_price_cents')
        .eq('id', athleteId)
        .maybeSingle(),
    ]);
    if (apRes.data?.paused) return;
    const recent = recentRes.data;
    if (!recent?.tier) return; // first-ever billing row stays a coach setup step

    const amountCents = apRes.data?.custom_price_cents ?? recent.amount_cents;
    const { error } = await supabase
      .from('billing')
      .insert({
        athlete_id: athleteId,
        month,
        year,
        tier: recent.tier,
        amount_cents: amountCents,
      });
    if (error) {
      console.warn('[plan-billing] could not create billing row:', error.message);
    }
  }

  async function handleSubmitPlan() {
    if (!plan) return;
    setPlanActionSaving(true);
    await supabase.from('plans').update({ status: 'sent' }).eq('id', plan.id);
    setPlan(prev => ({ ...prev, status: 'sent' }));
    setPlanActionSaving(false);
  }

  async function handleDeletePlan() {
    if (!plan) return;
    if (!window.confirm('Delete this plan? This will remove all workouts and cannot be undone.')) return;
    setPlanActionSaving(true);
    await supabase.from('plan_workouts').delete().eq('plan_id', plan.id);
    await supabase.from('athlete_plan_assignments').delete().eq('plan_id', plan.id);
    await supabase.from('plans').delete().eq('id', plan.id);
    setPlan(null);
    setWorkouts({});
    setCoachNotes({});
    setPlanActionSaving(false);
  }

  function openDay(dateStr) {
    if (!plan) return;
    setExpandedDay(dateStr);
    setEditText(workouts[dateStr] ?? '');
    setEditNote(coachNotes[dateStr] ?? '');
    setShowModal(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }

  function closeModal() {
    setShowModal(false);
    setExpandedDay(null);
  }

  async function handleModalSave() {
    if (expandedDay) await saveDay(expandedDay, editText, editNote);
    closeModal();
  }

  const saveDay = useCallback(async (dateStr, text, note) => {
    if (!plan) return;
    setSaving(true);

    // Check if row exists
    const { data: existing } = await supabase
      .from('plan_workouts')
      .select('id')
      .eq('plan_id', plan.id)
      .eq('workout_date', dateStr)
      .maybeSingle();

    const noteValue = (note ?? '').trim() || null;

    let saveError = null;
    if (existing) {
      const { error } = await supabase.from('plan_workouts')
        .update({ custom_description: text, coach_notes: noteValue })
        .eq('id', existing.id);
      saveError = error;
    } else {
      const { error } = await supabase.from('plan_workouts').insert({
        plan_id: plan.id,
        workout_date: dateStr,
        custom_description: text,
        coach_notes: noteValue,
      });
      saveError = error;
    }

    if (saveError) {
      console.error('[saveDay] save failed:', saveError.message);
      setSaving(false);
      return;
    }

    setWorkouts(prev => ({ ...prev, [dateStr]: text }));
    setCoachNotes(prev => {
      const n = { ...prev };
      if (noteValue) n[dateStr] = noteValue;
      else delete n[dateStr];
      return n;
    });
    setSaving(false);
  }, [plan]);

  // Build calendar cells
  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const firstDay = getFirstDayOfWeek(selectedYear, selectedMonth);
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - firstDay + 1;
    const isValid = dayNum >= 1 && dayNum <= daysInMonth;
    // Use JS Date to handle out-of-range dayNum (prev/next month) correctly
    const d = new Date(selectedYear, selectedMonth, dayNum);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    cells.push({ dayNum: d.getDate(), isValid, dateStr });
  }

  const years = [];
  for (let y = now.getFullYear() - 1; y <= now.getFullYear() + 2; y++) years.push(y);

  const athleteName = athletes.find(a => a.id === selectedAthleteId)?.full_name ?? '';

  // Returns { run, xt } miles for any workout text
  function vols(text) {
    return {
      run: parseRunningMiles(text) + parseTimedIntervalMiles(text, athletePaces),
      xt: parseCrossTrainingMiles(text, easyPaceSec),
      xtRange: easyPaceSec ? parseCrossTrainingMilesRange(text, easyPaceSec) : null,
    };
  }

  // Renders run/XT volume breakdown, or null if nothing
  // Pass xtRange to show a range display instead of a single total (e.g. for unlogged planned XT)
  function VolumeBreakdown({ run, xt, xtRange, label, style = {} }) {
    const total = (run || 0) + (xt || 0);
    if (total < 0.01) return null;
    const hasXt = (xt || 0) > 0.01;
    if (!hasXt) return (
      <span style={style}>{label ? `${label}: ` : ''}<strong>{formatVolume(total)}</strong></span>
    );
    const xtLabel = xtRange ? formatVolumeRange(xtRange) : formatVolume(xt);
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, ...style }}>
        {(run || 0) > 0.01 && <span style={{ color: 'var(--navy)' }}>Run: {formatVolume(run)}</span>}
        <span style={{ color: 'var(--gray-400)' }}>XT: {xtLabel}</span>
        {!xtRange && <strong>{label ? `${label}: ` : ''}{formatVolume(total)}</strong>}
      </span>
    );
  }

  function WeekVolumeLabel({ run, xt }) {
    const total = (run || 0) + (xt || 0);
    if (total < 0.01) return <>Week: <strong>0 mi</strong></>;
    const hasXt = (xt || 0) > 0.01;
    if (!hasXt) return <>Week: <strong>{formatVolume(total)}</strong></>;
    return (
      <>
        <span style={{ color: 'var(--navy)' }}>Run: {formatVolume(run)}</span>
        <span style={{ color: 'var(--gray-400)', margin: '0 5px' }}>·</span>
        <span style={{ color: 'var(--gray-400)' }}>XT: {formatVolume(xt)}</span>
        <span style={{ margin: '0 5px' }}>·</span>
        <strong>Week: {formatVolume(total)}</strong>
      </>
    );
  }

  // Build a date → vacation info map for day-cell indicators
  const vacationDateMap = {};
  for (const v of athleteVacations) {
    const d = new Date(v.start_date + 'T00:00:00');
    const last = new Date(v.end_date + 'T00:00:00');
    while (d <= last) {
      const ds = d.toISOString().slice(0, 10);
      if (!vacationDateMap[ds]) vacationDateMap[ds] = { blackout: false, vacId: v.id };
      if ((v.blackout_dates ?? []).includes(ds)) vacationDateMap[ds].blackout = true;
      d.setDate(d.getDate() + 1);
    }
  }

  return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <p className="page-eyebrow">Coaching</p>
          <h1 className="page-title">Plan Builder</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {saving && <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>Saving...</span>}
          {selectedAthleteId && (
            <button
              className="btn-xs btn-xs-ghost"
              onClick={() => navigate(`/coach/athletes/${selectedAthleteId}`)}
              title="Go to this athlete's training log"
            >
              ← View Log
            </button>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="plan-controls">
        <div className="form-group">
          <label className="form-label">Athlete</label>
          <select
            className="form-input form-select"
            value={selectedAthleteId}
            onChange={e => setSelectedAthleteId(e.target.value)}
          >
            {athletes.length === 0
              ? <option value="">No athletes yet</option>
              : athletes.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)
            }
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Month</label>
          <select
            className="form-input form-select"
            value={selectedMonth}
            onChange={e => setSelectedMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Year</label>
          <select
            className="form-input form-select"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {!loadingPlan && !plan && selectedAthleteId && (
          <div>
            <button className="btn btn-primary" style={{ width: 'auto', padding: '10px 20px' }} onClick={createPlan} disabled={saving}>
              {saving ? 'Creating...' : 'Create Plan'}
            </button>
            {planError && (
              <p style={{ marginTop: 8, fontSize: 12, color: 'var(--red, #dc2626)' }}>{planError}</p>
            )}
          </div>
        )}

        {plan && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--gray-400)', paddingBottom: 2 }}>
              {MONTHS[selectedMonth]} {selectedYear} — {athleteName}
            </span>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
              padding: '2px 10px', borderRadius: 12,
              background: plan.status === 'sent' ? '#dcfce7' : '#fef9c3',
              color: plan.status === 'sent' ? '#15803d' : '#92400e',
            }}>
              {plan.status === 'sent' ? 'Live' : 'In Progress'}
            </span>
            {plan.status !== 'sent' && (
              <button
                className="btn-xs btn-xs-primary"
                onClick={handleSubmitPlan}
                disabled={planActionSaving}
                title="Publish this plan — marks it as Live for the athlete and admin"
              >
                {planActionSaving ? '...' : 'Publish Plan'}
              </button>
            )}
            {plan.status === 'sent' && (
              <button
                className="btn-xs btn-xs-ghost"
                onClick={() => { setPlanActionSaving(true); supabase.from('plans').update({ status: 'in_progress' }).eq('id', plan.id).then(() => { setPlan(prev => ({ ...prev, status: 'in_progress' })); setPlanActionSaving(false); }); }}
                disabled={planActionSaving}
                title="Revert to In Progress"
              >
                Unpublish
              </button>
            )}
            <button
              className="btn-xs btn-xs-ghost"
              onClick={handleDeletePlan}
              disabled={planActionSaving}
              style={{ color: 'var(--gray-400)' }}
              title="Delete this plan entirely"
            >
              Delete Plan
            </button>
          </div>
        )}
      </div>

      {/* Active health alert for selected athlete */}
      {athleteHealthFlags.length > 0 && (
        <div style={{ marginBottom: 20, borderLeft: '3px solid #dc2626', background: '#fef2f2', borderRadius: 8, padding: '12px 20px' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 6 }}>
            Health Alert — {athleteName}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {athleteHealthFlags.map(f => (
              <div key={f.id} style={{ fontSize: 13, color: '#7f1d1d', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span>{f.type === 'injury' ? '🩹' : '🤒'}</span>
                <span>
                  {f.type === 'injury'
                    ? `${f.body_part ?? 'Injury'}${f.pain_scale != null ? ` — Pain ${f.pain_scale}/10` : ''}`
                    : (f.illness_type ?? 'Illness')}
                  {f.notes ? ` · "${f.notes}"` : ''}
                  <span style={{ color: '#b91c1c', marginLeft: 6, fontSize: 11 }}>since {f.flag_date}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vacation alert for selected athlete */}
      {athleteVacations.length > 0 && (() => {
        const EQUIP_LABELS = { track: 'Track', treadmill: 'Treadmill', roads: 'Roads/Trails', weight_room: 'Weight Room', pool: 'Pool', gym: 'Gym' };
        function fmtD(d) { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
        return (
          <div style={{ marginBottom: 20, borderLeft: '3px solid #f59e0b', background: '#fffbeb', borderRadius: 8, padding: '12px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 6 }}>
              Calendar & Events — {athleteName}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {athleteVacations.map(v => {
                const equip = (v.available_equipment ?? []).map(k => EQUIP_LABELS[k] ?? k);
                const blackouts = (v.blackout_dates ?? []).sort().map(fmtD);
                return (
                  <div key={v.id} style={{ fontSize: 13, color: '#78350f' }}>
                    <span style={{ fontWeight: 700 }}>{v.title || 'Trip'}</span>
                    <span style={{ fontWeight: 600 }}> · {fmtD(v.start_date)}{v.start_date !== v.end_date ? ` – ${fmtD(v.end_date)}` : ''}</span>
                    {equip.length > 0 && <span> · Available: {equip.join(', ')}</span>}
                    {equip.length === 0 && <span style={{ color: '#b45309' }}> · No equipment listed</span>}
                    {blackouts.length > 0 && <span> · No-run days: <strong>{blackouts.join(', ')}</strong></span>}
                    {v.notes && <span> · "{v.notes}"</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* No athletes */}
      {athletes.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-title">No athletes on your roster</p>
          <p className="empty-state-sub">Add athletes first via the Athletes page.</p>
        </div>
      )}

      {/* Loading */}
      {loadingPlan && (
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      )}

      {/* No plan yet */}
      {!loadingPlan && !plan && selectedAthleteId && athletes.length > 0 && (
        <div className="empty-state">
          <p className="empty-state-title">No plan for {athleteName} in {MONTHS[selectedMonth]} {selectedYear}</p>
          <p className="empty-state-sub">Click "Create Plan" above to start building.</p>
        </div>
      )}

      {/* Calendar */}
      {!loadingPlan && plan && (
        <div className="calendar-wrap">
          {/* Day headers */}
          <div className="calendar-grid">
            {DAYS.map(d => (
              <div key={d} className="calendar-header-cell">{d}</div>
            ))}
          </div>

          {/* Day cells — grouped by week with per-week totals */}
          {Array.from({ length: Math.ceil(cells.length / 7) }, (_, wi) => {
            const week = cells.slice(wi * 7, wi * 7 + 7);
            const weekVols = week.reduce((acc, cell) => {
              const text = cell.isValid
                ? (workouts[cell.dateStr] ?? '')
                : (boundaryWorkouts[cell.dateStr] ?? '');
              const v = vols(text);
              return { run: acc.run + v.run, xt: acc.xt + v.xt };
            }, { run: 0, xt: 0 });

            return (
              <div key={wi} className="calendar-grid calendar-week-row">
                {week.map((cell, i) => {
                  if (!cell.isValid) {
                    return <div key={i} className="day-cell day-cell--outside" />;
                  }

                  const dateStr = cell.dateStr;
                  const workout = workouts[dateStr] ?? '';
                  const today = isToday(selectedYear, selectedMonth, cell.dayNum);
                  const race = races[dateStr];
                  const vacInfo = vacationDateMap[dateStr];

                  return (
                    <div
                      key={dateStr}
                      className={`day-cell${today ? ' day-cell--today' : ''}${race ? ' day-cell--race' : ''}${vacInfo ? ' day-cell--vacation' : ''}${vacInfo?.blackout ? ' day-cell--blackout' : ''}`}
                      onClick={() => openDay(dateStr)}
                    >
                      <div className="day-number">{cell.dayNum}</div>
                      {race && (
                        <div className={`day-race-badge priority-${race.priority}`}>{race.priority} — {race.name}</div>
                      )}
                      {vacInfo && (
                        <div className={`day-vac-chip${vacInfo.blackout ? ' day-vac-chip--blackout' : ''}`}>
                          {vacInfo.blackout ? '✕ No run' : '✈ Travel'}
                        </div>
                      )}
                      {workout
                        ? <div className="day-preview">{workout}</div>
                        : <div className="day-preview day-preview--rest">Rest</div>
                      }
                      {(() => { const v = vols(workout); return <VolumeBreakdown run={v.run} xt={v.xt} xtRange={v.xtRange} style={{ fontSize: 11 }} />; })()}
                      {coachNotes[dateStr] && (
                        <div className="day-coach-note-badge">Note</div>
                      )}
                    </div>
                  );
                })}
                <div className="week-volume-row-total"><WeekVolumeLabel run={weekVols.run} xt={weekVols.xt} /></div>
              </div>
            );
          })}
          {(() => {
            const monthVols = Object.values(workouts).reduce((acc, text) => {
              const v = vols(text);
              return { run: acc.run + v.run, xt: acc.xt + v.xt };
            }, { run: 0, xt: 0 });
            const monthTotal = monthVols.run + monthVols.xt;
            const hasXt = monthVols.xt > 0.01;
            return (
              <div className="month-volume-total">
                {hasXt ? (
                  <>
                    <span style={{ color: 'var(--navy)' }}>Running: {formatVolume(monthVols.run) ?? '0 mi'}</span>
                    <span style={{ color: 'var(--gray-400)', margin: '0 6px' }}>·</span>
                    <span style={{ color: 'var(--gray-400)' }}>XT: {formatVolume(monthVols.xt)}</span>
                    <span style={{ margin: '0 6px' }}>·</span>
                    <strong>Month: {formatVolume(monthTotal) ?? '0 mi'}</strong>
                  </>
                ) : (
                  <>Month total: <strong>{formatVolume(monthTotal) ?? '0 mi'}</strong></>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Day Modal */}
      {showModal && expandedDay && (
        <div className="day-modal-overlay" onClick={closeModal}>
          <div className="day-modal" onClick={e => e.stopPropagation()}>
            <div className="day-modal-header">
              <div>
                <div className="day-modal-date">{formatModalDate(expandedDay)}</div>
                {races[expandedDay] && (
                  <div className={`day-race-badge priority-${races[expandedDay].priority}`} style={{ marginTop: 6 }}>
                    {races[expandedDay].priority} — {races[expandedDay].name}
                  </div>
                )}
              </div>
              <button className="day-modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="day-modal-body">
              {expandedDay && vacationDateMap[expandedDay] && (() => {
                const EQUIP_LABELS = { track: 'Track', treadmill: 'Treadmill', roads: 'Roads/Trails', weight_room: 'Weight Room', pool: 'Pool', gym: 'Gym' };
                const vac = athleteVacations.find(v => v.id === vacationDateMap[expandedDay].vacId);
                const isBlackout = vacationDateMap[expandedDay].blackout;
                const equip = (vac?.available_equipment ?? []).map(k => EQUIP_LABELS[k] ?? k);
                return (
                  <div style={{ background: isBlackout ? '#fef2f2' : '#fffbeb', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: isBlackout ? '#991b1b' : '#92400e', borderLeft: `3px solid ${isBlackout ? '#dc2626' : '#f59e0b'}` }}>
                    {isBlackout
                      ? '✕ No-run day — athlete cannot train today.'
                      : <>✈ Travel day{equip.length > 0 ? ` · Available: ${equip.join(', ')}` : ' · No equipment listed'}{vac?.notes ? ` · "${vac.notes}"` : ''}</>
                    }
                  </div>
                );
              })()}
              {athletePaces && (
                <div className="pace-cheat-sheet">
                  <div className="pace-cheat-title">Pace Reference</div>
                  <div className="pace-cheat-grid">
                    {PACE_TAGS.filter(({ zone }) => athletePaces[zone]?.perMile).map(({ tag, label, zone }) => (
                      <div key={tag} className="pace-cheat-row">
                        <span className="pace-cheat-tag">{tag}</span>
                        <span className="pace-cheat-pace">{athletePaces[zone].perMile}<span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>/mi</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pace-tag-toolbar">
                {PACE_TAGS.map(({ tag, label }) => (
                  <button
                    key={tag}
                    className="pace-tag-btn"
                    onMouseDown={e => {
                      e.preventDefault();
                      insertTagAtCursor(textareaRef.current, tag, setEditText);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <textarea
                ref={textareaRef}
                className="day-modal-textarea"
                value={editText}
                onChange={e => setEditText(e.target.value)}
                placeholder="Write today's workout... Use pace tags like [EASY] or [TEMPO]"
              />
              {(() => {
                const v = vols(editText);
                const total = v.run + v.xt;
                if (total < 0.01) return null;
                return (
                  <div className="day-modal-volume">
                    <VolumeBreakdown run={v.run} xt={v.xt} xtRange={v.xtRange} label="Volume" />
                  </div>
                );
              })()}

              <div className="day-modal-note-section">
                <div className="day-modal-note-label">Coach note <span className="day-modal-note-hint">(visible to athlete)</span></div>
                <textarea
                  className="day-modal-note-textarea"
                  value={editNote}
                  onChange={e => setEditNote(e.target.value)}
                  placeholder="Add context or instructions for this workout..."
                  rows={2}
                />
              </div>

            </div>

            <div className="day-modal-footer">
              <button className="btn-xs btn-xs-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-xs btn-xs-primary" onClick={handleModalSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
